import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, ChevronDown, Search, ArrowLeft } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import HeroBanner from "../components/HeroBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [config, setConfig] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/faqs`).then(res => res.json()),
      fetch(`${API_URL}/api/config`).then(res => res.json())
    ])
      .then(([faqsData, configData]) => {
        setFaqs(faqsData);
        setConfig(configData);
        setLoading(false);
      })
      .catch(console.error);
  }, []);

  const categories = [
    { id: "all", label: "Toutes" },
    { id: "intervention", label: "Intervention" },
    { id: "tarifs", label: "Tarifs" },
    { id: "technique", label: "Technique" },
    { id: "entreprise", label: "Entreprise" }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === "all" || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0f2744]"></div>
      </div>
    );
  }

  return (
    <div data-testid="faq-page">
      {/* Hero */}
      <HeroBanner
        title="Questions Fréquentes Fichet"
        subtitle="Retrouvez les réponses aux questions les plus courantes sur nos services de dépannage de serrures Fichet."
        highlightText="Fichet"
        showFeatures={false}
        showCTA={false}
        config={config}
        variant="compact"
        breadcrumb={
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Accueil
          </Link>
        }
      />

      {/* Search and filters */}
      <section className="py-8 bg-white border-b border-gray-100 sticky top-16 md:top-20 z-40">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une question..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="faq-search"
              />
            </div>

            {/* Category filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeCategory === cat.id
                      ? "bg-[#0f2744] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  data-testid={`faq-category-${cat.id}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">
                  Aucune question trouvée pour cette recherche.
                </p>
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setActiveCategory("all");
                  }}
                  className="text-amber-600 font-medium hover:underline"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            ) : (
              <Accordion type="single" collapsible className="space-y-4">
                {filteredFaqs.map((faq, index) => (
                  <AccordionItem
                    key={faq.id}
                    value={faq.id}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                    data-testid={`faq-item-${index}`}
                  >
                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-gray-50 [&[data-state=open]>svg]:rotate-180">
                      <span className="text-[#0f2744] font-semibold pr-4">
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 text-gray-600">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>

          {/* Still have questions */}
          <div className="max-w-3xl mx-auto mt-12 bg-gradient-to-br from-[#0f2744] to-[#1a3a5c] text-white rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">
              Vous ne trouvez pas votre réponse ?
            </h2>
            <p className="text-gray-300 mb-6">
              Appelez-nous directement, notre équipe se fera un plaisir de vous renseigner.
            </p>
            {config && (
              <a
                href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                className="btn-cta inline-flex items-center gap-2"
                data-testid="faq-cta-phone"
              >
                <Phone className="w-5 h-5" />
                {config.phone}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Schema.org FAQ structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })
      }} />
    </div>
  );
}
